// Oryx icon sprites for the interface: class icons, item icons and world props by *pick name*
// (public/assets/oryx/picks.json) or by raw frame name. Atlases load once and are shared with
// HeroSprite's creature loader. Falls back to a glyph until the atlas is served.
import { classLook } from '../../game/heroKit';
import { useEffect, useState, type ReactNode } from 'react';
import { HeroSprite } from './HeroSprite';

interface Frame { x: number; y: number; w: number; h: number }
interface Atlas { frames: Record<string, Frame>; size: { w: number; h: number }; url: string }
const atlases = new Map<string, Promise<Atlas>>();
let picks: Promise<Record<string, string>> | undefined;

function atlasFor(prefix: string): string | undefined {
  if (prefix === 'class') return 'classes';
  if (prefix === 'item') return 'items';
  if (prefix === 'world') return 'world';
  if (prefix === 'creature') return 'creatures';
  if (prefix === 'fx24' || prefix === 'fx32' || prefix === 'tiles') return prefix;
  return undefined;
}

function loadAtlas(key: string): Promise<Atlas> {
  let p = atlases.get(key);
  if (!p) {
    p = fetch(`/assets/oryx/${key}.json`).then(async (r) => {
      if (!r.ok) throw new Error(`atlas ${key} ${r.status}`);
      const j = (await r.json()) as { frames: Record<string, { frame: Frame }>; meta?: { size?: { w: number; h: number } } };
      return { frames: Object.fromEntries(Object.entries(j.frames).map(([k, v]) => [k, v.frame])), size: j.meta?.size ?? { w: 512, h: 512 }, url: `/assets/oryx/${key}.png` };
    });
    atlases.set(key, p);
  }
  return p;
}

function loadPicks(): Promise<Record<string, string>> {
  picks ??= fetch('/assets/oryx/picks.json').then(async (r) => (r.ok ? ((await r.json()) as Record<string, string>) : {})).catch(() => ({}));
  return picks;
}

/** Resolves a pick name (`item_coin_bag`) or frame name (`item_072`) to its atlas frame. */
export function useOryxFrame(name: string | undefined): { frame: Frame; atlas: Atlas } | undefined {
  const [res, setRes] = useState<{ frame: Frame; atlas: Atlas }>();
  useEffect(() => {
    let live = true;
    setRes(undefined);
    if (!name) return;
    (async () => {
      const p = await loadPicks();
      const raw = typeof p[name] === 'string' ? p[name]! : name;
      const key = atlasFor(raw.split('_')[0]!);
      if (!key) return;
      const atlas = await loadAtlas(key).catch(() => undefined);
      const frame = atlas?.frames[raw];
      if (live && atlas && frame) setRes({ frame, atlas });
    })();
    return () => { live = false; };
  }, [name]);
  return res;
}

export const ICON_GLYPH: Record<string, string> = {
  item_coin_bag: '◉', item_heart: '♥', item_key_gold: '⚷', item_scroll: '✎', item_sword: '⚔', item_bow: '➶', item_shield_round: '⛨', item_potion_red: '⚗', item_potion_blue: '⚗',
  chest_gold_closed: '▣', chest_gold_open: '▣', class_icon_fighter: '⚔', class_icon_rogue: '🗡', class_icon_wizard: '✦', class_icon_cleric: '✚', class_icon_ranger: '➶', class_icon_paladin: '☀', class_icon_druid: '♣', class_icon_bard: '♪',
};

/** An Oryx frame as a CSS sprite at an integer scale; `framed` puts it on an inset tile. `hue`
 * (degrees) recolours the sprite alone (docs/72: a catalogue piece's `hue`), never its frame. */
export function OryxIcon({ name, scale = 2, framed, title, glyph, className = '', hue }: { name: string; scale?: 1 | 2 | 3 | 4; framed?: boolean; title?: string; glyph?: ReactNode; className?: string; hue?: number }) {
  const hit = useOryxFrame(name);
  const cls = `fk-sprite ${framed ? 'fk-sprite--framed' : ''} ${className}`;
  if (!hit) return <span className={`${cls} fk-sprite--glyph`} style={{ width: 16 * scale, height: 16 * scale, fontSize: 9 * scale }} role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true} title={title}>{glyph ?? ICON_GLYPH[name] ?? '•'}</span>;
  const { frame: f, atlas } = hit;
  const sprite = { width: f.w * scale, height: f.h * scale, backgroundImage: `url(${atlas.url})`, backgroundPosition: `-${f.x * scale}px -${f.y * scale}px`, backgroundSize: `${atlas.size.w * scale}px ${atlas.size.h * scale}px` };
  if (!hue) return <span className={cls} role="img" aria-label={title ?? name} title={title} style={sprite} />;
  return (
    <span className={cls} role="img" aria-label={title ?? name} title={title} data-hue={hue} style={{ width: f.w * scale, height: f.h * scale, display: 'inline-block', position: 'relative' }}>
      <span className="fk-sprite__hued" style={{ ...sprite, position: 'absolute', inset: 0, filter: `hue-rotate(${hue}deg)` }} />
    </span>
  );
}

export const CLASS_ICON: Record<string, string> = { fighter: 'class_icon_fighter', rogue: 'class_icon_rogue', wizard: 'class_icon_wizard', cleric: 'class_icon_cleric', ranger: 'class_icon_ranger', paladin: 'class_icon_paladin', druid: 'class_icon_druid', bard: 'class_icon_bard' };

export function ClassIcon({ classId, scale = 1, title }: { classId: string; scale?: 1 | 2; title?: string }) {
  return <OryxIcon name={CLASS_ICON[classId] ?? 'class_icon_fighter'} scale={scale} title={title ?? classId} />;
}

/** A figure on a portrait tile (roster, level-up, dialogue). A hero (`heroId`, or a class with no frame: a class's
 *  portrait) is drawn from the hero kit (docs/145); an NPC by its Oryx frame. */
export function Portrait({ frame, classId, scale = 3, glow, title, className = '', heroId, species }: { frame?: number; classId?: string; scale?: 2 | 3 | 4; glow?: boolean; title?: string; className?: string; heroId?: bigint | number; species?: number }) {
  const look = heroId === undefined && !frame && classId ? classLook(classId, species) : undefined;
  return <span className={`fk-portrait ${scale >= 3 ? 'fk-portrait--lg' : ''} ${glow ? 'fk-portrait--glow' : ''} ${className}`} style={{ padding: scale >= 3 ? 6 * (scale - 2) : 4 }}><HeroSprite heroId={heroId} look={look} species={species} frame={frame} classId={classId} scale={scale} title={title} /></span>;
}
